import { $isListItemNode } from '@lexical/list';
import { $findMatchingParent } from '@lexical/utils';
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  type LexicalNode,
  type ParagraphNode,
} from 'lexical';

import type { ExcerptContext, TextBlock } from '@/teoCore/TeoApi';

import type { TeoMarker } from '../EditorMarkerManager.svelte';
import { $isTeoTextNode, TeoTextNode } from './TeoTextNode';

export interface OffsetInNode {
  node: LexicalNode;
  offset: number;
}

export interface OffsetTeoMarker {
  start: OffsetInNode;
  end: OffsetInNode;
  marker: TeoMarker;
}

/**
 * Checks if the start of the current selection in the editor is within a node of the specified type.
 *
 * @param nodeType - The name of the type to check the node for.
 * @returns `true` if the current selection starts within a node of the specified type, otherwise `false`.
 */
export function $currentSelectionStartsInNodeType(nodeType: string): boolean {
  const selection = $getSelection();
  if (selection === null) return false;
  const startEnd = selection.getStartEndPoints();
  if (startEnd === null) return false;
  const startNode = startEnd[0].getNode();
  if (startNode.getType() === nodeType) return true;
  const parentMatchingNode = $findMatchingParent(
    startNode,
    (ancestor) => ancestor.getType() === nodeType,
  );
  return parentMatchingNode !== null;
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Tries to preserve the structure of paragraphs and line breaks with good readability.
 */
export function $getPlainText(node: LexicalNode): string {
  let textString = '';

  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if ($isTeoTextNode(node)) {
        textString += node.getTextContent();
      } else if ($isListItemNode(node)) {
        textString += '- ';
      } else if ($isLineBreakNode(node)) {
        textString += '\n';
      } else if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        if ($isParagraphNode(node)) {
          textString += '\n\n';
        } else {
          textString += '\n';
        }
      }
    },
  );
  return textString.trim();
}

/**
 * Creates a string representation of the content of a LexicalNode.
 * Keeps the character offsets consistent.
 */
export function $getTeoTextContent(node: LexicalNode): string {
  let textString: string = '';
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      if (!$isElementNode(node)) {
        textString += node.getTextContent();
      }
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textString += '\n\n';
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the string
  return textString.replace(/\s+$/g, '');
}

export function $getTextArray(node: LexicalNode): TextBlock[] {
  const textArray: TextBlock[] = [];
  $traverseNodes(
    node,
    (node: LexicalNode) => {
      let newTextBlock: TextBlock | undefined = undefined;
      if ($isTeoTextNode(node)) {
        const string = node.getTextContent();
        if (string.length > 0) {
          newTextBlock = {
            string: string,
            type: 'text',
          };
        }
      } else if ($isListItemNode(node)) {
        newTextBlock = {
          string: '',
          type: 'text',
          length: 0,
          interpret_as: '\n- ',
        };
      } else if ($isLineBreakNode(node)) {
        newTextBlock = {
          string: '',
          type: 'linebreak',
          length: node.getTextContentSize(),
        };
      } else if (!$isElementNode(node)) {
        newTextBlock = {
          string: node.getTextContent(),
          type: 'text',
        };
        if (!['tab'].includes(node.getType())) {
          console.warn('Handling unknown node type in $getTextArray:', node.getType());
        }
      } else {
        if (!['root', 'paragraph'].includes(node.getType())) {
          console.warn('Ignoring unknown node type in $getTextArray:', node.getType());
        }
      }
      if (newTextBlock) {
        textArray.push(newTextBlock);
      }
    },
    // postCallback:
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        textArray.push({
          string: '\n\n',
          type: 'paragraph',
          length: 2,
        });
      }
    },
  );
  // Trim trailing paragraph and linebreak blocks from the end of the array
  while (
    textArray.length > 0 &&
    (textArray[textArray.length - 1]?.type === 'paragraph' ||
      textArray[textArray.length - 1]?.type === 'linebreak')
  ) {
    textArray.pop();
  }
  return textArray;
}

type MarkerContextPiece = {
  text: string;
  markers: { id: string; type: string }[];
};

/**
 * Live excerpt around a marker.
 * `text` spans the first-to-last node of the wrapper marker so inserts inside the highlight stay
 * in the excerpt. Returns `undefined` when the focused marker is not attached.
 */
export function $getMarkerContext(
  rootNode: LexicalNode,
  focusId: string,
  parentType?: string,
): ExcerptContext | undefined {
  const pieces: MarkerContextPiece[] = [];
  $traverseNodes(
    rootNode,
    (node: LexicalNode) => {
      if ($isElementNode(node)) return;
      pieces.push({
        text: node.getTextContent(),
        markers:
          $isTeoTextNode(node) ?
            node.markers.map((marker) => ({ id: marker.id, type: marker.type }))
          : [],
      });
    },
    (node: LexicalNode) => {
      if ($isElementNode(node) && !node.isInline()) {
        pieces.push({ text: '\n\n', markers: [] });
      }
    },
  );

  const trimmedPieces = trimTrailingWhitespacePieces(pieces);
  const hasFocus = trimmedPieces.some((piece) => pieceHasMarker(piece, focusId));
  if (!hasFocus) return;

  const wrapperId = resolveWrapperMarkerId(trimmedPieces, focusId, parentType);
  const firstWrapper = trimmedPieces.findIndex((piece) => pieceHasMarker(piece, wrapperId));
  const lastWrapper = trimmedPieces.findLastIndex((piece) => pieceHasMarker(piece, wrapperId));
  if (firstWrapper === -1 || lastWrapper === -1) return;

  return {
    text: joinPieces(trimmedPieces.slice(firstWrapper, lastWrapper + 1)),
    textBefore: joinPieces(trimmedPieces.slice(0, firstWrapper)),
    textAfter: joinPieces(trimmedPieces.slice(lastWrapper + 1)),
    textFocus: joinPieces(trimmedPieces.filter((piece) => pieceHasMarker(piece, focusId))),
    replacementWrapper: wrapperId,
  };
}

function pieceHasMarker(piece: MarkerContextPiece, markerId: string): boolean {
  return piece.markers.some((marker) => marker.id === markerId);
}

function resolveWrapperMarkerId(
  pieces: MarkerContextPiece[],
  focusId: string,
  parentType?: string,
): string {
  if (!parentType) return focusId;
  const parent = pieces
    .filter((piece) => pieceHasMarker(piece, focusId))
    .flatMap((piece) => piece.markers)
    .find((marker) => marker.type.startsWith(parentType));
  return parent?.id ?? focusId;
}

function joinPieces(pieces: MarkerContextPiece[]): string {
  return pieces.map((piece) => piece.text).join('');
}

function trimTrailingWhitespacePieces(pieces: MarkerContextPiece[]): MarkerContextPiece[] {
  const full = joinPieces(pieces);
  let remaining = full.replace(/\s+$/g, '').length;
  const trimmed: MarkerContextPiece[] = [];
  for (const piece of pieces) {
    if (remaining <= 0) break;
    if (piece.text.length <= remaining) {
      trimmed.push(piece);
      remaining -= piece.text.length;
    } else {
      trimmed.push({ ...piece, text: piece.text.slice(0, remaining) });
      remaining = 0;
    }
  }
  return trimmed;
}

/**
 * Traverses a LexicalNode tree in pre-order (node, then children), with optional post-order callback.
 *
 * @param node - The root LexicalNode to start traversal from.
 * @param callback - Function called on each node before its children.
 *                   If it returns false, traversal of that branch is stopped.
 * @param postCallback - Optional function called on each node after its children (post-order).
 *
 * @returns false if traversal was stopped early, otherwise undefined.
 */
export function $traverseNodes(
  node: LexicalNode,
  callback: (node: LexicalNode) => boolean | void,
  postCallback?: (node: LexicalNode) => void,
) {
  const shouldContinue = callback(node);
  if (shouldContinue === false) return false;
  if ($isElementNode(node)) {
    node.getChildren().forEach((child: LexicalNode) => {
      $traverseNodes(child, callback, postCallback);
    });
  }
  postCallback?.(node);
}

export function $getLeafNodes(node: LexicalNode): LexicalNode[] {
  if (!$isElementNode(node)) {
    return [node];
  }
  const nodes: LexicalNode[] = [];
  node.getChildren().forEach((child: LexicalNode) => {
    nodes.push(...$getLeafNodes(child));
  });
  return nodes;
}

/**
 * Builds the paragraphs of a plain text: blank lines separate paragraphs, single newlines
 * become line breaks. `$getTeoTextContent` turns them back into the same text.
 */
export function $createParagraphs(content: string): ParagraphNode[] {
  return content.split('\n\n').map((block) => {
    const paragraph = $createParagraphNode();
    block.split('\n').forEach((line, index) => {
      if (index > 0) paragraph.append($createLineBreakNode());
      if (line) paragraph.append($createTextNode(line));
    });
    return paragraph;
  });
}

/**
 * Merges neighbouring text nodes that look the same and carry the same markers, and removes
 * empty unmarked ones. Lexical does neither for `TeoTextNode`, because it is not simple text,
 * so the splits made for markers would pile up from one analysis to the next.
 */
export function $compactTeoTextNodes(rootNode: LexicalNode): void {
  $getLeafNodes(rootNode).forEach((leaf) => {
    if (!$isTeoTextNode(leaf)) return;
    // Merging and removing rewrite the neighbours, so the collected leaf may be outdated.
    const node = leaf.getLatest();
    if (!node.isAttached()) return;
    if (node.getTextContent() === '') {
      const hasSibling = node.getPreviousSibling() !== null || node.getNextSibling() !== null;
      if (node.markers.length === 0 && hasSibling) node.remove();
      return;
    }
    const previous = node.getPreviousSibling();
    if ($isTeoTextNode(previous) && $canMergeTeoTextNodes(previous, node)) {
      previous.mergeWithSibling(node);
    }
  });
}

function $canMergeTeoTextNodes(a: TeoTextNode, b: TeoTextNode): boolean {
  return (
    a.getMode() === 'normal' &&
    b.getMode() === 'normal' &&
    !a.isUnmergeable() &&
    !b.isUnmergeable() &&
    a.getFormat() === b.getFormat() &&
    a.getStyle() === b.getStyle() &&
    a.getDetail() === b.getDetail() &&
    a.markers.length === b.markers.length &&
    a.markers.every((marker) => b.markers.some((other) => other.id === marker.id))
  );
}

/**
 * Transforms a `TeoMarker` (which uses absolute offsets) into an `OffsetTeoMarker`
 * (which uses node-relative offsets) by traversing the given Lexical tree.
 *
 * This function walks the tree rooted at `rootNode`, mapping the absolute start and end
 * offsets of the marker to specific text nodes and their corresponding offsets.
 *
 * @param marker - The `TeoMarker` object containing absolute start and end offsets.
 * @param rootNode - The root `LexicalNode` of the tree to traverse.
 * @returns An `OffsetTeoMarker` with node-relative offsets if mapping is successful,
 *          or `null` if the marker could not be mapped (e.g., out of bounds).
 */
export function $transformMarkerToOffsetMarker(
  marker: TeoMarker,
  rootNode: LexicalNode,
): OffsetTeoMarker | null {
  let start: OffsetInNode | null = null;
  let end: OffsetInNode | null = null;
  let lastTextNode: TeoTextNode | null = null;
  let currentOffset = 0;

  // Traversal callback
  function visit(node: LexicalNode): boolean | void {
    let nextOffset = currentOffset;
    if (!$isElementNode(node)) {
      nextOffset += node.getTextContentSize();
    }

    if ($isTeoTextNode(node)) {
      if (!start && marker.start < nextOffset) {
        const offsetInNode = marker.start - currentOffset;
        start = {
          node,
          offset: Math.max(offsetInNode, 0),
        };
      }
      if (!end && marker.end < nextOffset) {
        const offsetInNode = marker.end - currentOffset;
        end = {
          node: offsetInNode > 0 || lastTextNode === null ? node : lastTextNode,
          offset: offsetInNode > 0 ? offsetInNode : (lastTextNode?.getTextContentSize() ?? 0),
        };
        if (start && end) return false; // Stop traversal
      }
      lastTextNode = node;
    }

    currentOffset = nextOffset;
  }

  // Post-order callback
  function postVisit(node: LexicalNode) {
    if (!node.isInline()) {
      currentOffset += 2;
    }
  }

  $traverseNodes(rootNode, visit, postVisit);

  // Normal case: both start and end found
  if (start && end) {
    return { start, end, marker };
  }

  // Edge case: marker ends at the very end of the text
  if (start && marker.end <= currentOffset && lastTextNode) {
    return {
      start,
      end: {
        node: lastTextNode,
        offset: (lastTextNode as TeoTextNode).getTextContentSize(),
      },
      marker,
    };
  }

  console.warn(
    'Invalid Marker:',
    marker,
    'Last offset:',
    currentOffset,
    'Last text node:',
    lastTextNode,
  );
  return null;
}
